import React, { useEffect, useMemo, useState } from 'react';
import { useEntryFinder } from '~Components/EntryFinder';
import { useDeviceInfo, useLogger, useSelector } from '~Hooks';
import { CompleteBongQuery_venue_company_categories_items as Item } from '~Scenes/POS/Queries/__queries__';
import useBongQuery from '~Scenes/POS/Queries/query';
import useCategories, { ItemCategoryMap } from '~Scenes/POS/categories';
import useI18n from '~Translations';

export interface Category {
  id: string;
  color: string;
  label: string;
}

export interface POSButton {
  id: string;
  type: 'PAGE' | 'ITEM' | 'OFFER';
  nodeId: string;
  color: string;
  label: string;
  x: number | null;
  y: number | null;
}

interface Loading {
  isLoading: boolean;
  progress: number;
  progressText: string;
}

interface Breadcrumb {
  id: string;
  name: string;
}

/** Sentinel id for the root category crumb, so users can navigate back out of the first sub-page. */
export const ROOT_BREADCRUMB_ID = 'root';

export interface Context {
  columnCount: number | null;
  categories: Array<Category>;
  buttons: Array<POSButton>;
  selectedCategoryId: string;
  setSelectedCategoryId: (id: string) => void;
  itemIdMap: Record<string, Item>;
  loading: Loading | null;

  breadcrumbs: Breadcrumb[];

  itemToCategoryMap: ItemCategoryMap;

  browseToSubpage: (page: Breadcrumb) => void;
  browseToBreadcrumb: (crumb: Breadcrumb) => void;
}

const BongLayoutContext = React.createContext<Context>({
  columnCount: null,
  buttons: [],
  categories: [],
  selectedCategoryId: '',
  setSelectedCategoryId: (id: string) => {},
  itemIdMap: {},
  loading: null,

  itemToCategoryMap: {},

  breadcrumbs: [],
  browseToSubpage: () => {},
  browseToBreadcrumb: () => {},
});

/**
 * The purpose of this Provider is to handle all the logic for the layout of
 * the bong / ordering flow.
 *
 * There's a LOT of data being passed around, and its kinda messy. So instead
 * of letting the mess live everywhere, the mess has been contained to this file.
 *
 * Important info: "useCategories" is an even messier hook that fetches data from backend.
 *
 * A lot of the complexity comes from the fact that the layout can be either
 * a) A simple list of categories, with each page being the items of that category, or
 * b) A manually designed layout, with buttons that can be placed anywhere on the grid
 *    and sub-pages that lead to separate pages.
 *
 * And this provider creates an API for merging these two concepts into one.
 *
 * @returns
 */
export const BongLayoutProvider: React.FC = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [subPage, setSubPage] = useState<string | null>(null);
  const deviceInfo = useDeviceInfo();

  const demoMode = useSelector((state) => state.demoMode);
  let venueId = useSelector((state) => state.venueId);
  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }

  const findEntryFromItem = useEntryFinder();
  const { data, loading, progress, progressText } = useBongQuery(venueId);

  const {
    allItems,
    categories,
    layoutCategory,
    selectedCategory,
    setSelectedCategory,
    categoryItems,
    layout,
    itemToCategoryMap,
    mapItem,
  } = useCategories(data, []);

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: BongLayoutProvider');
    return () => {
      mountLogger.info('Unmounted: BongLayoutProvider');
    };
  }, []);

  const itemIdMap = useMemo(() => {
    const map: Record<string, Item> = {};

    allItems.forEach((item) => {
      map[item.id] = item;
    });

    return map;
  }, [allItems]);

  const filteredItems = categoryItems.filter((item) => {
    const entry = findEntryFromItem(item.id);

    return !!entry;
  });

  let buttons: POSButton[] = filteredItems.map((item) => ({
    id: item.id,
    type: 'ITEM',
    nodeId: item.id,
    color: item.color || layoutCategory?.color || '#0f0',
    label: item.title,

    // These are not used when not using layouts
    x: null,
    y: null,
  }));

  let gridTemplateAreas: string | null = null;
  let resolvedPage = layoutCategory?.page ?? null;

  if (layoutCategory) {
    gridTemplateAreas = '';

    let page = layoutCategory.page;
    if (subPage) {
      const pageOnCompany = data?.company.pages.find((p) => p.id === subPage);

      // We only assign if we actually find it, so its not so disastrous if we not
      if (pageOnCompany) {
        page = pageOnCompany;
      }
    }

    resolvedPage = page;

    buttons = page.buttons
      .filter((b) => b.title.length)
      .filter((b) => {
        // If its not an item, dont check if it exists in the menu
        if (!b.itemId) {
          return true;
        }

        const entry = findEntryFromItem(b.itemId);

        return !!entry;
      })
      .map((b) => {
        // If the color is set on the specific button, use that
        let color = b.color;

        const item = itemIdMap[b.itemId];

        // If not color is set and its an item, use the color from the item's category
        if (!color && item) {
          color = mapItem(item)?.color;
        }

        // If still no color is set, use the color from the layout category, or fall back
        color = color || layoutCategory?.color || '#fafafa';

        return {
          id: b.id,
          type: b.itemId ? 'ITEM' : b.offerId ? 'OFFER' : 'PAGE',
          nodeId: b.itemId || b.pageId || b.offerId || '',
          color,
          label: b.title,

          // The X and Y position of the button in the grid
          // CSS uses 1-indexed values, so we add 1 to the values from the API
          x: b.x + 1,
          y: b.y + 1,
        };
      });
  }

  function browseToSubpage(crumb: Breadcrumb) {
    setSubPage(crumb.id);

    // When navigating into the first sub-page, prepend a crumb for the root category
    // so the user can still see and click their way back to it.
    if (breadcrumbs.length === 0) {
      setBreadcrumbs([{ id: ROOT_BREADCRUMB_ID, name: selectedCategory?.name ?? '' }, crumb]);
    } else {
      setBreadcrumbs([...breadcrumbs, crumb]);
    }
  }

  function browseToBreadcrumb(crumb: Breadcrumb) {
    if (crumb.id === ROOT_BREADCRUMB_ID) {
      setSubPage(null);
      setBreadcrumbs([]);
      return;
    }

    const index = breadcrumbs.findIndex((c) => c.id === crumb.id);

    if (index === -1) {
      return;
    }

    setSubPage(crumb.id);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  }

  const calculatePossibleRows = () => {
    // 120 is the height of the buttons wrapper at the bottom, on a phone we need some more space due to the phones top and bottom bar
    const extraSpace = deviceInfo?.isPhone ? 220 : 120;

    // 48 is the height of a button
    const maxRows = Math.floor((window.innerHeight - extraSpace) / 48);
    return maxRows;
  };

  return (
    <BongLayoutContext.Provider
      value={{
        columnCount: resolvedPage?.columns || 3,
        itemIdMap,
        buttons,
        breadcrumbs,
        itemToCategoryMap,
        browseToBreadcrumb,
        browseToSubpage,
        categories: categories
          .map((c) => ({
            id: c.id,
            color: c.color || '#ffe',
            label: c.name,
          }))
          .slice(0, calculatePossibleRows()),
        selectedCategoryId: selectedCategory?.id || '',
        setSelectedCategoryId: (id) => {
          // Typescript is throwing a fit here for some reason
          const category = (categories as any).find((c: { id: string }) => c.id === id);

          setSubPage(null);
          setBreadcrumbs([]);
          setSelectedCategory(category || null);
        },
        loading: {
          isLoading: loading,
          progress: progress || 0,
          progressText: progressText || '',
        },
      }}>
      {children}
    </BongLayoutContext.Provider>
  );
};

interface MockProps {
  data: Partial<Context>;
}

export const MockedBongLayoutProvider: React.FC<MockProps> = ({ children, data }) => {
  const fullData: Context = {
    columnCount: 3,
    itemIdMap: {},
    breadcrumbs: [],
    browseToBreadcrumb: () => {},
    browseToSubpage: () => {},
    buttons: [],
    categories: [],
    selectedCategoryId: '1',
    setSelectedCategoryId: () => {},
    itemToCategoryMap: {},
    loading: null,
    ...data,
  };

  const { i18n } = useI18n();
  const findEntryFromItem = useEntryFinder();
  const filteredItems = useMemo(() => {
    return fullData.buttons.filter((item) => {
      const entry = findEntryFromItem(item.id);

      return !!entry;
    });
  }, [data.buttons]);

  return (
    <BongLayoutContext.Provider
      value={{
        ...fullData,
        buttons: filteredItems,
        loading: {
          isLoading: false,
          progress: 20,
          progressText: i18n('General.LoadingDots'),
        },
      }}>
      {children}
    </BongLayoutContext.Provider>
  );
};

export const useBongLayout = () => {
  const value = React.useContext(BongLayoutContext);

  if (!value.itemIdMap) {
    throw new Error('useBongLayout must be used within a BongLayoutProvider');
  }

  return value;
};
